/**
 * useAvailabilityAnalytics.js
 *
 * Analytics hook for the "Check Availability" page.
 * Mirrors useWelcomeAnalytics.js exactly — same GA4 wrapper via react-ga4,
 * same IS_PRODUCTION guard (excludes localhost + 127.0.0.1), same dev-mode
 * console.group/console.table logging so events are inspectable without
 * opening the GA4 dashboard.
 *
 * HOW TO USE:
 *   const analytics = useAvailabilityAnalytics();
 *   analytics.trackDateSelect("2026-08-19");
 *
 * DEV MODE:
 *   All events are console.group'd with the [AvailabilityAnalytics] prefix.
 *   Open browser DevTools → Console to see all events in real time.
 */

import { useCallback } from "react";
import ReactGA from "react-ga4";

// ─── Production guard ──────────────────────────────────────────────────────────
// Matches useWelcomeAnalytics.js: excludes both localhost and 127.0.0.1 since
// the CRA dev server can serve on either hostname.
const IS_PRODUCTION =
  process.env.NODE_ENV === "production" &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost" &&
  window.location.hostname !== "127.0.0.1";

// ─── Core send helper ──────────────────────────────────────────────────────────
const send = (eventName, params = {}) => {
  // Strip undefined / null / empty values so the GA4 payload stays clean
  const cleanParams = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

  if (IS_PRODUCTION) {
    ReactGA.event(eventName, cleanParams);
  } else {
    // eslint-disable-next-line no-console
    console.group(`%c[AvailabilityAnalytics] ${eventName}`, "color:#3d6b4f;font-weight:bold;");
    // eslint-disable-next-line no-console
    if (Object.keys(cleanParams).length > 0) console.table(cleanParams);
    // eslint-disable-next-line no-console
    else console.log("(no params)");
    // eslint-disable-next-line no-console
    console.groupEnd();
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════════

const useAvailabilityAnalytics = () => {
  // ── Page lifecycle ───────────────────────────────────────────────────────────

  const trackPageView = useCallback((fromStep) => {
    send("availability_page_view", { from_step: fromStep });
  }, []);

  // ── Selection steps ──────────────────────────────────────────────────────────

  const trackDateSelect = useCallback((isoDate) => {
    send("availability_date_select", { date: isoDate });
  }, []);

  const trackTimeSelect = useCallback((time) => {
    send("availability_time_select", { time });
  }, []);

  // ── Form submission ──────────────────────────────────────────────────────────

  const trackFormSubmit = useCallback(({ status, date, time }) => {
    send("availability_form_submit", { status, date, time });
  }, []);

  return {
    trackPageView,
    trackDateSelect,
    trackTimeSelect,
    trackFormSubmit,
  };
};

export default useAvailabilityAnalytics;